import { pipe } from "@mobily/ts-belt"
import type { CardAttachment } from "@shared/models/card-stack"
import { isApple } from "@shared/unified-tiptap/util/is-apple"
import AiChanges from "@tiptap-pro/extension-ai-changes"
import type { EditorEvents } from "@tiptap/core"
import { Editor } from "@tiptap/core"
import { Bold } from "@tiptap/extension-bold"
import type { BubbleMenuOptions } from "@tiptap/extension-bubble-menu"
import BubbleMenu from "@tiptap/extension-bubble-menu"
import { Document } from "@tiptap/extension-document"
import HardBreak from "@tiptap/extension-hard-break"
import Highlight from "@tiptap/extension-highlight"
import Image from "@tiptap/extension-image"
import { Italic } from "@tiptap/extension-italic"
import { Link } from "@tiptap/extension-link"
import { Paragraph } from "@tiptap/extension-paragraph"
import { Strike } from "@tiptap/extension-strike"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { Text } from "@tiptap/extension-text"
import { Color, TextStyle } from "@tiptap/extension-text-style"
import Typography from "@tiptap/extension-typography"
import { Underline } from "@tiptap/extension-underline"
import { Dropcursor, UndoRedo } from "@tiptap/extensions"
import type { Node, ParseOptions } from "@tiptap/pm/model"
import { Fragment, Slice } from "@tiptap/pm/model"
import type { EditorView } from "@tiptap/pm/view"
import type Filestack from "cc-frontend/services/filestack"
import "cc-frontend/utils/polyfills/array-find-last"
import {
  FlatListCore,
  FlatListOrdered,
  FlatListTask,
  FlatListUnordered,
  JoinListDOMSerializer,
} from "tiptap-extension-flat-list"
import TrailingBreak, { addTrailingBreaks } from "tiptap-extension-trailing-break"

export const HIGHLIGHT_COLORS = [
  // Lighter versions of the card colors (--section-color-red etc.), converted to rgb values due to
  // a variant of https://github.com/ueberdosis/tiptap/issues/1818
  // that was causing them to convert when the AI output them.
  "rgb(245, 214, 216)",
  "rgb(247, 225, 212)",
  "rgb(251, 251, 182)",
  "rgb(225, 236, 198)",
  "rgb(207, 227, 217)",
  "rgb(214, 238, 245)",
  "rgb(207, 222, 237)",
  "rgb(217, 212, 237)",
  "rgb(235, 209, 225)",
] as const

export const TEXT_COLORS = [
  // Same colors as the card colors (--section-color-red etc.), converted to rgb values due to
  // https://github.com/ueberdosis/tiptap/issues/1818 .
  "rgb(216, 90, 98)",
  "rgb(216, 113, 49)",
  "rgb(207, 163, 42)",
  "rgb(133, 164, 55)",
  "rgb(104, 166, 135)",
  "rgb(23, 151, 186)",
  "rgb(95, 138, 180)",
  "rgb(120, 111, 159)",
  "rgb(164, 70, 128)",
] as const

/**
 * Image to use while a pasted image is uploading to filestack.
 * For now we just let it show up as the alt text.
 */
const uploadingImageSrc = ""

const PARSE_OPTIONS: ParseOptions = {
  // Preserve series of multiple spaces from previous instances of CcTiptap.
  preserveWhitespace: true,
}

/**
 * Class that creates and configures TipTap with our chosen extensions and behaviors.
 *
 * This class does not handle non-TipTap-initiated changes (in particular, toolbar operations);
 * see ScribeEditor instead.
 *
 * Global styles used by this class are in styles/lib/_tiptap.scss.
 */
export class CcTiptap {
  readonly editor: Editor

  private readonly addLink: (editor: Editor) => void
  private readonly filestack: Filestack
  private readonly addAttachments?: (files: Omit<CardAttachment, "id">[]) => void

  // ============
  // Tiptap config
  // ============

  constructor(
    element: Element,
    html: string | null,
    options: {
      addLink: (editor: Editor) => void
      filestack: Filestack
      bubbleMenuOptions: Partial<BubbleMenuOptions>
      addAttachments?: (files: Omit<CardAttachment, "id">[]) => void
      onTransaction?: (props: EditorEvents["transaction"]) => void
      onUpdate?: (props: EditorEvents["update"]) => void
      class?: string
    }
  ) {
    this.editor = new Editor({
      element,
      autofocus: false,
      parseOptions: PARSE_OPTIONS,
      editorProps: {
        attributes: { class: (options.class || "") + " cc-tiptap" },
        transformPasted: this.transformPasted.bind(this),
        handlePaste: this.handlePaste.bind(this),
        handleDrop: this.handleDrop.bind(this),
        handleKeyDown: this.handleKeyDown.bind(this),
      },
      // Use onCreate instead of content so that appendTransaction hooks are called.
      // Needed until we fix https://github.com/commoncurriculum/tiptap-extension-flat-list/issues/7
      onCreate: () => this.setHTML(html),
      onTransaction: options.onTransaction,
      onUpdate: options.onUpdate,
      extensions: [
        // Basics
        Document,
        Text,
        // Functionality
        UndoRedo,
        Typography,
        Dropcursor,
        BubbleMenu.configure(options.bubbleMenuOptions),
        AiChanges,
        // Block types
        Paragraph,
        FlatListCore,
        FlatListOrdered.configure({
          getListStyleType: (indent) => {
            // GDocs convention.
            return ["decimal", "lower-alpha", "lower-roman"][indent % 3]
          },
        }),
        FlatListUnordered.configure({
          getListStyleType: (indent) => {
            // GDocs convention.
            return ["disc", "circle", "square"][indent % 3]
          },
        }),
        FlatListTask,
        // Marks
        Italic,
        Bold,
        Underline,
        Strike,
        Subscript,
        Superscript,
        Highlight.configure({ multicolor: true }), // Before TextStyle due to https://github.com/ueberdosis/tiptap/issues/3427
        TextStyle,
        Color,
        Link.configure({
          autolink: true,
          defaultProtocol: "https",
          linkOnPaste: true,
          openOnClick: true,
        }).extend({
          // Don't extend a link when typing after it.
          inclusive: false,
          exitable: true,
          // Additional functionality in this._handleKeyDown (Mod+K keybinding).
          // If we wanted to be proper, we could instead put that in a plugin that goes here.
        }),
        // Non-text inline content
        HardBreak,
        TrailingBreak,
        Image.configure({
          // Inline for compatibility with lessons generated by Ckeditor.
          // It would be nice to migrate away from this at some point.
          inline: true,
          // Base64 is allowed so that Tiptap can parse pasted/dropped images.
          // They are converted to filestack before actually reaching the editor state -
          // see this.transformPasted and this.handleDrop.
          allowBase64: true,
        }),
      ],
    })
    JoinListDOMSerializer.setClipboardSerializer(this.editor)

    this.addLink = options.addLink
    this.filestack = options.filestack
    this.addAttachments = options.addAttachments
  }

  destroy(): void {
    this.editor.destroy()
  }

  // ============
  // Event handlers
  // ============

  private nextUploadId = 1

  /**
   * Uploads the given image to Filestack, eventually replacing the given node
   * (if still present in the document). Also modifies node (in-place!) to appear as
   * a placeholder for the image while uploading.
   *
   * @param image Image data in a form usable by this.filestack.client.upload,
   * e.g., a data: URL or File.
   */
  private uploadImageWithPlaceholder(node: Node, image: string | File): Node {
    // Replace node with a filler image before pasting.
    const oldAlt = node.attrs.alt
    const tempAlt = `Uploading image ${this.nextUploadId++}...`
    const newNode = node.type.create(
      { ...node.attrs, src: uploadingImageSrc, alt: tempAlt },
      node.content,
      node.marks
    )

    // Upload the image and then replace the placeholder node.
    this.filestack.client.upload(image).then((upload) => {
      const pos = findPos(
        this.editor.state.doc,
        (n) =>
          n.type.name === Image.name && n.attrs.src === uploadingImageSrc && n.attrs.alt === tempAlt
      )
      if (pos === null) return // node was deleted
      this.editor.commands.command(({ tr }) => {
        tr.setNodeAttribute(pos, "src", upload.url)
        tr.setNodeAttribute(pos, "alt", oldAlt)
        tr.setMeta("addToHistory", false)
        return true
      })
    })

    return newNode
  }

  private transformPasted(slice: Slice): Slice {
    let newContent = slice.content

    // Replace any base64 images inside the content with a placeholder while uploading them to filestack.
    // (Images pasted directly as DataTransfer files are instead handled by handlePaste.)
    // Also, remove any file:// images (sometimes pasted from MSWord). These don't make sense
    // in the app because they are local files, and they somehow cause the print service to crash.
    newContent = replaceNodes(newContent, (node) => {
      if (node.type.name === Image.name) {
        const src: string | null = node.attrs.src
        if (src !== null) {
          if (src.match("data:image") !== null) {
            return this.uploadImageWithPlaceholder(node, src)
          } else if (src.startsWith("file://")) {
            return null
          }
        }
      }
      return node
    })

    // Restrict text and highlight colors to the ones we allow in the toolbar.
    newContent = replaceNodes(newContent, (node) => {
      let newMarks = node.marks

      const styleMark = node.marks.find((mark) => mark.type.name === TextStyle.name)
      if (styleMark) {
        if (styleMark.attrs.color !== undefined && !TEXT_COLORS.includes(styleMark.attrs.color)) {
          const newStyleMark = styleMark.type.create({ ...styleMark.attrs, color: undefined })
          newMarks = styleMark.removeFromSet(newMarks)
          newMarks = newStyleMark.addToSet(newMarks)
        }
      }

      const highlightMark = node.marks.find((mark) => mark.type.name === Highlight.name)
      if (highlightMark) {
        if (!HIGHLIGHT_COLORS.includes(highlightMark.attrs.color)) {
          newMarks = highlightMark.removeFromSet(newMarks)
          if (!highlightMark.attrs.color) {
            // The user pasted a plain <mark> tag, which is traditionally yellow. Change it to an allowed yellow.
            const newHighlightMark = highlightMark.type.create({
              color: "rgb(251, 251, 182)" satisfies (typeof HIGHLIGHT_COLORS)[number],
            })
            newMarks = newHighlightMark.addToSet(newMarks)
          }
        }
      }

      if (newMarks !== node.marks) return node.mark(newMarks)
      else return node
    })

    return new Slice(newContent, slice.openStart, slice.openEnd)
  }

  private handlePaste(_view: EditorView, event: ClipboardEvent): boolean {
    // Based on extension-file-handler source code.
    if (event.clipboardData && event.clipboardData.files.length > 0) {
      this.handleDataTransfer(event.clipboardData)
      // If there is also HTML content, let ProseMirror handle it normally.
      return !event.clipboardData.getData("text/html")
    }
    return false
  }

  private handleDrop(view: EditorView, event: DragEvent): boolean {
    // Based on extension-file-handler source code.
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.handleDataTransfer(
        event.dataTransfer,
        view.posAtCoords({ left: event.x, top: event.y })?.pos
      )
      return true
    }
    return false
  }

  /**
   * Called by handlePaste and handleDrop to process DataTransfer files
   * (files dropped/pasted as files instead of as HTML content).
   *
   * - If it's an image, insert a placeholder and upload it (like transformPasted).
   * - Otherwise, upload it to filestack and call addAttachments. In normal lesson editing,
   * this attaches the file to the card.
   *
   * @param pos The insertion position. If not specified, inserts at the current selection.
   */
  private handleDataTransfer(dataTransfer: DataTransfer, pos?: number): void {
    const imageFiles = [...dataTransfer.files].filter((file) => file.type.startsWith("image/"))
    const otherFiles = [...dataTransfer.files].filter((file) => !file.type.startsWith("image/"))

    if (imageFiles.length > 0) {
      Promise.all(imageFiles.map(fileToDataURL)).then((urls) => {
        // Insert placeholders for the images, like when pasting.
        const nodes = urls.map((url) => {
          const imageNode = this.editor.schema.nodes[Image.name].create({})
          return this.uploadImageWithPlaceholder(imageNode, url)
        })
        const content = nodes.map((node) => node.toJSON())
        if (pos !== undefined) {
          this.editor.chain().insertContentAt(pos, content).focus().run()
        } else {
          this.editor.commands.insertContent(content)
        }
      })
    }

    if (otherFiles.length > 0 && this.addAttachments) {
      this.filestack.client.multiupload(otherFiles).then((uploaded) => {
        const files = this.filestack.asAttachments(uploaded)
        this.addAttachments!(files)
      })
    }
  }

  private handleKeyDown(_view: EditorView, event: KeyboardEvent): boolean {
    // Mod+K -> open Add Link dialog.
    if (event.key === "k" && !event.shiftKey && !event.altKey) {
      if (
        (isApple && event.metaKey && !event.ctrlKey) ||
        (!isApple && event.ctrlKey && !event.metaKey)
      ) {
        event.preventDefault()
        this.addLink(this.editor)
        return true
      }
    }

    return false
  }

  // ============
  // API
  // ============

  get isEmpty(): boolean {
    // this.editor.isEmpty will return true if the text contains a single empty bullet,
    // but we only want truly empty content.
    const doc = this.editor.state.doc
    // Just the root
    if (doc.nodeSize === 2) return true
    // Single empty paragraph
    if (doc.nodeSize === 4) {
      const onlyChild = doc.child(0)
      return onlyChild.type.name === Paragraph.name
    }
    return false
  }

  getHTML() {
    // Map empty => "" (like unedited) to make it easier to check emptiness of the HTML.
    if (this.isEmpty) return ""
    return pipe(
      this.editor.state.doc,
      (doc) => addTrailingBreaks(doc),
      (doc) => JoinListDOMSerializer.getDocHTML(doc),
      (html) => {
        // Mark the generated HTML as coming from Tiptap instead of Ckeditor.
        // Also, apply ProseMirror's white-space settings to the HTML so that spaces are preserved
        // when we render it in other places (e.g. comment emails),
        // from https://github.com/ProseMirror/prosemirror-view/blob/master/style/prosemirror.css .
        // Note: This gets lost, collapsing whitespace, in a reverse migration (Tiptap to Ckeditor) - okay.
        return `<div data-cc-tiptap-html style="white-space: pre-wrap; white-space: break-spaces;">${html}</div>`
      }
    )
  }

  setHTML(html: string | null) {
    return this.editor.commands.command(({ tr, dispatch, commands }) => {
      if (dispatch) {
        // Don't add set operations to Mod-Z undo history.
        // The same tr is used by the command below, so it's respected there.
        tr.setMeta("addToHistory", false)
      }

      return commands.setContent(preprocessHTML(html), {
        emitUpdate: false,
        parseOptions: PARSE_OPTIONS,
      })
    })
  }

  /**
   * Returns whether an HTML value comes from CcTiptap.getHTML (as opposed to Ckeditor).
   */
  static isCcTiptapHtml(html: string): boolean {
    return html.startsWith("<div data-cc-tiptap-html")
  }
}

function preprocessHTML(html: string | null): string {
  if (html === null) return ""
  if (!CcTiptap.isCcTiptapHtml(html)) {
    // html is probably from Ckeditor.
    // Remove non-visible newlines and tabs that Ckeditor adds throughout the HTML.
    // Tiptap parses these as spaces (due to preserveWhitespace: true).
    html = html.replace(/[\n\t]/g, "")
    // Replace empty paragraphs propped up by an &nbsp with a "true" empty paragraph.
    // This is what ProseMirror expects; otherwise, you get an extra space.
    html = html.replaceAll("<p>&nbsp;</p>", "<p></p>")
  }
  return html
}

/**
 * Returns the position of the first descendant satisfying predicate, or null if none is found.
 */
function findPos(doc: Node, predicate: (node: Node) => boolean): number | null {
  let found: number | null = null
  doc.descendants((node, pos) => {
    if (found !== null) return false
    if (predicate(node)) {
      found = pos
      return false
    }
    return true
  })
  return found
}

/**
 * Replaces each node in fragment's subtree with the result of calling replace on that node
 * (followed by recursing into the node's content).
 *
 * Return null to remove a node entirely.
 */
function replaceNodes(fragment: Fragment, replace: (node: Node) => Node | null): Fragment {
  let hasChange = false
  const newChildren: Node[] = []
  for (let i = 0; i < fragment.childCount; i++) {
    const child = fragment.child(i)
    let newChild = replace(child)
    if (newChild !== null) {
      // Recurse.
      const newChildContent = replaceNodes(child.content, replace)
      if (newChildContent !== child.content) {
        newChild = newChild.copy(newChildContent)
      }

      newChildren.push(newChild)
    }

    if (newChild !== child) hasChange = true
  }

  if (hasChange) return Fragment.fromArray(newChildren)
  else return fragment
}

/**
 * Convert an image File to a data:image URL.
 */
function fileToDataURL(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      resolve(event.target!.result as string)
    }
    reader.onerror = (error) => {
      reject(error)
    }
    reader.readAsDataURL(file)
  })
}
